import { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react'

import { INotification } from '../services/Notification/interfaces'
import { NotificationService } from '../services/Notification/NotificationService'

const POLL_INTERVAL = 30000
const STORAGE_KEY = 'notified_notification_ids'
const MAX_STORED_IDS = 200

type NotificationContextType = {
  notifications: INotification[]
  unreadCount: number
  markAsRead: (id: number) => Promise<void>
  markAllAsRead: () => Promise<void>
  refreshNotifications: () => void
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined)

function getStoredNotifiedIds(): Set<number> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      const parsed = JSON.parse(stored)
      if (Array.isArray(parsed)) {
        return new Set(parsed.filter((id): id is number => typeof id === 'number'))
      }
    }
  } catch {
    // ignore parse errors
  }
  return new Set()
}

function storeNotifiedIds(ids: Set<number>): Set<number> {
  const arr = Array.from(ids)
  const trimmed = arr.length > MAX_STORED_IDS ? arr.slice(arr.length - MAX_STORED_IDS) : arr
  localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed))
  return new Set(trimmed)
}

function sendBrowserNotification(notification: INotification) {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return

  try {
    new Notification(notification.title, {
      body: notification.description,
      icon: '/favicon.ico'
    })
  } catch {
    // Notification API may fail silently in some environments
  }
}

export function NotificationProvider({ children }: { children: ReactNode }) {
  const [notifications, setNotifications] = useState<INotification[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const lastTimestampRef = useRef<string>(new Date(0).toISOString())
  const notifiedIdsRef = useRef<Set<number>>(getStoredNotifiedIds())
  const isFirstLoadRef = useRef(true)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const cancelledRef = useRef(false)
  const isPollingRef = useRef(false)
  const serviceRef = useRef(new NotificationService())

  const poll = useCallback(async () => {
    const token = localStorage.getItem('token')
    if (!token) return
    if (isPollingRef.current) return

    isPollingRef.current = true

    try {
      const data = await serviceRef.current.poll(lastTimestampRef.current)

      if (cancelledRef.current) return

      setUnreadCount(data.unread_count)
      lastTimestampRef.current = data.last_timestamp

      if (data.notifications.length > 0) {
        setNotifications((prev) => {
          const merged = [...data.notifications, ...prev]
          const unique = merged.filter((n, i, arr) => arr.findIndex((m) => m.id === n.id) === i)
          return unique.slice(0, 10)
        })

        if (isFirstLoadRef.current) {
          // First load: store IDs but don't fire browser notifications
          data.notifications.forEach((n) => notifiedIdsRef.current.add(n.id))
          notifiedIdsRef.current = storeNotifiedIds(notifiedIdsRef.current)
        } else {
          // Subsequent polls: fire browser notifications for new IDs
          data.notifications.forEach((n) => {
            if (!notifiedIdsRef.current.has(n.id)) {
              sendBrowserNotification(n)
              notifiedIdsRef.current.add(n.id)
            }
          })
          notifiedIdsRef.current = storeNotifiedIds(notifiedIdsRef.current)
        }
      }
    } catch {
      // Silently handle errors; the next poll will retry
    } finally {
      isFirstLoadRef.current = false
      isPollingRef.current = false
      if (!cancelledRef.current) {
        timeoutRef.current = setTimeout(poll, POLL_INTERVAL)
      }
    }
  }, [])

  useEffect(() => {
    cancelledRef.current = false

    // Request browser notification permission
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      Notification.requestPermission()
    }

    poll()

    return () => {
      cancelledRef.current = true
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [poll])

  const markAsRead = useCallback(async (id: number) => {
    await serviceRef.current.markAsRead(id)
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, is_read: true, read_at: new Date().toISOString() } : n))
    )
    setUnreadCount((prev) => Math.max(0, prev - 1))
  }, [])

  const markAllAsRead = useCallback(async () => {
    await serviceRef.current.markAllAsRead()
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true, read_at: new Date().toISOString() })))
    setUnreadCount(0)
  }, [])

  const refreshNotifications = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    poll()
  }, [poll])

  return (
    <NotificationContext.Provider
      value={{ notifications, unreadCount, markAsRead, markAllAsRead, refreshNotifications }}
    >
      {children}
    </NotificationContext.Provider>
  )
}

export function useNotifications() {
  const context = useContext(NotificationContext)
  if (!context) {
    throw new Error('useNotifications deve ser usado dentro de um NotificationProvider')
  }
  return context
}
